import React, { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import axiosAdmin from "./AdminAxios";

/**
 * Decode JWT payload without verifying signature (verification is done server-side)
 * @param {string} token - JWT token
 * @returns {object|null} Decoded payload or null if invalid
 */
const decodeToken = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};

/**
 * Quick client-side check if token structure is valid
 * @param {string} token - JWT token
 * @returns {boolean} True if token appears valid
 */
const isTokenValidClientSide = (token) => {
    if (!token) return false;

    const payload = decodeToken(token);
    if (!payload) return false;

    // Check role
    if (payload.role !== 'admin') return false;

    // Check expiration
    if (payload.exp) {
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp < now) {
            localStorage.removeItem("adminToken");
            return false;
        }
    }

    return true;
};

/**
 * AdminRoute - Protects routes that require admin authentication
 *
 * Security layers:
 * 1. Client-side: Quick check of token structure and expiration
 * 2. Server-side: Validates token signature and ensures backend is reachable
 */
const AdminRoute = ({ children }) => {
    const [authState, setAuthState] = useState('checking'); // 'checking' | 'authorized' | 'unauthorized'

    useEffect(() => {
        const verifyToken = async () => {
            const token = localStorage.getItem("adminToken");

            // Step 1: Quick client-side validation
            if (!isTokenValidClientSide(token)) {
                if (token) localStorage.removeItem("adminToken");
                setAuthState('unauthorized');
                return;
            }

            // Step 2: Server-side validation (ensures backend is reachable + token is authentic)
            try {
                await axiosAdmin.get('/admin/verify-token');
                setAuthState('authorized');
            } catch (error) {
                console.error("Admin token verification failed:", error.message);
                // Token invalid or backend unreachable
                localStorage.removeItem("adminToken");
                setAuthState('unauthorized');
            }
        };

        verifyToken();
    }, []);

    // Loading state while checking
    if (authState === 'checking') {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500 mx-auto mb-4"></div>
                    <p className="text-amber-500 font-mono">Vérification des accès admin...</p>
                </div>
            </div>
        );
    }

    // Redirect if not authorized
    if (authState === 'unauthorized') {
        return <Navigate to="/admin" replace />;
    }

    // Authorized - render children
    return children;
};

export default AdminRoute;
